import type { ArgDef, VarDef } from '../models';

export type ArgValueType = ArgDef['type'] | VarDef['type'];

/**
 * Coerce a value to the type its argument/variable definition declares.
 *
 * The player gates a ref on `attributes` and compares **strictly**, with no
 * conversion (`attributes[group] !== argValue`). `player.args` carries real
 * types, so an attribute stored as the string `"true"` can never match the
 * boolean `true` — the ref is skipped and the scene silently never plays.
 * DEV-749.
 *
 * Canonical representation is the real JSON type, not a normalised string:
 * `inferArgType` derives an argDef's type from `typeof val`, so stringifying
 * attributes would make `boolean` un-discoverable and effectively delete the
 * concept.
 *
 * **One known duplicate:** `features/publisher/embed-code.util.ts` inlines the
 * same rule. It is exercised directly by `node --test`, which cannot resolve an
 * extensionless relative import, while `ng build` rejects the `.ts` extension
 * that would fix that — so it cannot import this file today. Keep them in sync.
 *
 * Deliberately total — an unparseable number keeps its original string rather
 * than becoming `NaN`, which would be worse than the value we started with.
 */
export function coerceToArgType(value: unknown, type: ArgValueType): string | number | boolean {
	if (type === 'boolean') return value === true || value === 'true';
	if (type === 'number') {
		if (typeof value === 'number') return Number.isFinite(value) ? value : String(value);
		const n = Number(value);
		return value !== '' && value != null && Number.isFinite(n) ? n : String(value ?? '');
	}
	return value == null ? '' : String(value);
}

/** Typed `{ key: value }` map of form entries; empty values are left out. */
export function argsMapOf(
	entries: readonly { key: string; type: string; value: string }[],
): Record<string, string | number | boolean> {
	const map: Record<string, string | number | boolean> = {};
	for (const e of entries) {
		if (e.value !== '') map[e.key] = coerceToArgType(e.value, e.type as ArgValueType);
	}
	return map;
}

/** The value a freshly-attached definition of this type should carry. */
export function emptyValueForArgType(type: ArgValueType): string | number | boolean {
	switch (type) {
		case 'number':
			return 0;
		case 'boolean':
			return false;
		case 'string':
		case 'variant':
		default:
			return '';
	}
}
